/**
 * Migrate a record to .docx the first time someone opens it.
 *
 * The bulk rake converts everything known ahead of time. This covers the long
 * tail — a document nobody has opened in a year — so no cutoff date has to
 * decide whose history is worth keeping.
 *
 * WHY IT MATTERS: without this, an unmigrated body only RENDERS. The browser
 * fallback in `useResolvedDocxBody` converts legacy HTML with no migration
 * context, so the document appears WITHOUT its comments and suggestions,
 * nothing is persisted, and `document_revisions` is never touched. Editing it
 * then saves that context-free conversion and orphans the collaboration anchors
 * permanently. Running the real migration first avoids all of it.
 *
 * The mutation is idempotent, so a double open is harmless and no locking is
 * needed. On failure the editor still opens on the render-only fallback —
 * degraded, but never blocked.
 */

import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { mutations } from '../../../../api';

/** A body is already .docx when it starts with the ZIP magic. */
function looksLikeDocx(body: unknown): boolean {
  const s = String(body ?? '');
  return s.startsWith('UEsD') || s.startsWith('PK');
}

export interface UseLazyDocxMigrationOptions {
  documentId?: string | number;
  /** 'Document' | 'DocumentTemplate' — the polymorphic type the backend expects. */
  documentType: 'Document' | 'DocumentTemplate';
  /** The stored body blob (`{ body, body_storage_format }`). */
  bodySource?: Record<string, unknown> | null;
  /** Re-read the record once it has been converted. */
  onMigrated: () => void | Promise<void>;
  enabled?: boolean;
}

/**
 * @returns `migrating` — true while the conversion is in flight, so the caller
 * can hold the editor back rather than mounting it on the pre-migration body
 * and remounting a moment later.
 */
export function useLazyDocxMigration({
  documentId,
  documentType,
  bodySource,
  onMigrated,
  enabled = true,
}: UseLazyDocxMigrationOptions): { migrating: boolean } {
  const client = useApolloClient();
  const [migrating, setMigrating] = useState(false);
  // One attempt per record per mount. A failed conversion must not retry in a
  // loop against a body the converter can't handle.
  const attemptedRef = useRef<string | null>(null);
  // The record on screen now, and the conversion whose completion may clear
  // `migrating`. A conversion's outcome is NOT tied to the effect run that
  // started it: the body it converts changes underneath it (the page's query
  // re-reads the record, and `onMigrated` refetches it), which re-runs the
  // effect. Cancelling on that cleanup left `migrating` true forever — the
  // editor's mount gate never opened and a freshly imported document showed a
  // blank page until reload.
  const activeKeyRef = useRef<string | null>(null);
  const inFlightKeyRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const body = bodySource?.body;
  const format = String(bodySource?.body_storage_format ?? '');

  useEffect(() => {
    const key = documentId ? `${documentType}:${documentId}` : null;
    activeKeyRef.current = key;
    if (!enabled || !key) return;
    if (attemptedRef.current === key) return;

    const hasBody = typeof body === 'string' && body.length > 0;
    if (!hasBody) return;
    if (format === 'docx' || looksLikeDocx(body)) return;

    attemptedRef.current = key;
    inFlightKeyRef.current = key;
    setMigrating(true);

    (async () => {
      try {
        const { data } = await client.mutate({
          mutation: mutations.migrateRecordToDocx,
          variables: { documentId: String(documentId), documentType },
        });
        const result = data?.migrateRecordToDocx;
        // Reloading is for the record that was converted; if the user has moved
        // on, the record now on screen must not be re-resolved under them.
        if (mountedRef.current && activeKeyRef.current === key && result?.migrated) await onMigrated();
      } catch (err) {
        // Never block opening. The render-only fallback still shows the
        // document; it just won't carry comments or suggestions this session.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[lazy-docx-migration] failed', err);
        }
      } finally {
        if (mountedRef.current && inFlightKeyRef.current === key) {
          inFlightKeyRef.current = null;
          setMigrating(false);
        }
      }
    })();
    // `onMigrated` is a caller-owned callback; re-running on its identity would
    // re-migrate on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, documentId, documentType, body, format, enabled]);

  return { migrating };
}
